import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Tag,
  Typography
} from "antd";
import { FilePdfOutlined, MailOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatDay,
  formatEuro
} from "../workReports/format";
import {
  createGuarantee,
  createReturnLetter,
  deleteGuarantee,
  emailReturnLetter,
  getGuarantee,
  replaceRetention,
  retentionCandidates,
  returnLetterUrl,
  setGuaranteeStatus,
  updateGuarantee
} from "./api";
import {
  GUARANTEE_COLORS,
  basisLabels,
  directionLabels,
  guaranteeStatusLabels,
  guaranteeTypeLabels,
  reasonLabels,
  sourceLabel
} from "./labels";

const day = value => (value ? dayjs(value) : null);
const iso = value => (value ? value.format("YYYY-MM-DD") : "");

/** Status change with a date (and the amount for "drawn"). */
const StatusDialog = ({ open, status, guarantee, onClose, onDone }) => {
  const { t, i18n } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open)
      form.setFieldsValue({ on: dayjs(), amount: centsToEuro(guarantee?.amount), note: "" });
  }, [open]);
  const submit = async values => {
    setBusy(true);
    try {
      const res = await setGuaranteeStatus(guarantee._id, {
        status,
        on: iso(values.on),
        amount: status === "drawn" ? euroToCents(values.amount) : undefined,
        note: values.note
      });
      onDone(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      rootClassName="warranty-page"
      open={open}
      title={guaranteeStatusLabels(t)[status]}
      okText={t("Save")}
      cancelText={t("Cancel")}
      confirmLoading={busy}
      onOk={() => form.submit()}
      onCancel={onClose}
    >
      <Form form={form} layout="vertical" onFinish={submit}>
        <Form.Item name="on" label={t("Date")} rules={[{ required: true }]}>
          <DatePicker className="wty-full" format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
        {status === "drawn" && (
          <Form.Item name="amount" label={t("Amount drawn")} rules={[{ required: true }]}>
            <InputNumber
              className="wty-full"
              min={0.01}
              precision={2}
              addonAfter="€"
              {...decimalInputProps(i18n.language)}
            />
          </Form.Item>
        )}
        <Form.Item name="note" label={t("Note")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

/**
 * Create or edit a Bürgschaft; for an existing one also its return date, fee, the return
 * letter, status changes and the replacement of retentions.
 */
const GuaranteeModal = ({ open, id, meta, project, onClose, onChanged }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [form] = Form.useForm();
  const [guarantee, setGuarantee] = useState(null);
  const [busy, setBusy] = useState(false);
  const [statusDialog, setStatusDialog] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [chosen, setChosen] = useState([]);
  const direction = Form.useWatch("direction", form);
  const type = Form.useWatch("type", form);
  const projectId = Form.useWatch("project", form);
  const labels = guaranteeStatusLabels(t);
  const reasons = reasonLabels(t);

  const fill = g =>
    form.setFieldsValue({
      direction: g.direction,
      type: g.type,
      issuerName: g.issuer?.name,
      issuerAddress: g.issuer?.address,
      bondNumber: g.bondNumber,
      amount: centsToEuro(g.amount),
      beneficiary: g.beneficiary,
      principal: g.principal,
      client: g.client || undefined,
      supplier: g.supplier || undefined,
      project: g.project || undefined,
      warranty: g.warranty || undefined,
      contractBasis: g.contractBasis,
      issueDate: day(g.issueDate),
      expiryDate: day(g.expiryDate),
      handedOverOn: day(g.handedOverOn),
      agreedMonths: g.returnTerms?.agreedMonths || null,
      agreedDate: day(g.returnTerms?.agreedDate),
      conditionMetOn: day(g.returnTerms?.conditionMetOn),
      feePercent: g.fee?.rateBp ? g.fee.rateBp / 100 : null,
      note: g.note
    });

  useEffect(() => {
    if (!open) return;
    setGuarantee(null);
    setChosen([]);
    form.resetFields();
    if (id) {
      getGuarantee(id)
        .then(res => {
          setGuarantee(res.data.data);
          fill(res.data.data);
        })
        .catch(generalErrorAlert);
    } else {
      form.setFieldsValue({
        direction: "given",
        type: "warranty",
        contractBasis: "vob",
        project: project || undefined
      });
    }
  }, [open, id]);

  useEffect(() => {
    if (
      open &&
      guarantee &&
      meta?.can.replaceRetention &&
      guarantee.direction === "given" &&
      guarantee.type === "warranty"
    ) {
      retentionCandidates(guarantee.project)
        .then(res => setCandidates(res.data.data))
        .catch(() => setCandidates([]));
    }
  }, [open, guarantee?._id]);

  const changed = data => {
    setGuarantee(data);
    fill(data);
    onChanged?.();
  };

  const submit = async values => {
    setBusy(true);
    const body = {
      direction: values.direction,
      type: values.type,
      issuer: { name: values.issuerName, address: values.issuerAddress },
      bondNumber: values.bondNumber,
      amount: euroToCents(values.amount),
      beneficiary: values.beneficiary,
      principal: values.principal,
      client: values.client || null,
      supplier: values.supplier || null,
      project: values.project || null,
      warranty: values.warranty || null,
      contractBasis: values.contractBasis,
      issueDate: iso(values.issueDate),
      expiryDate: iso(values.expiryDate),
      handedOverOn: iso(values.handedOverOn),
      returnTerms: {
        agreedMonths: Number(values.agreedMonths || 0),
        agreedDate: iso(values.agreedDate),
        conditionMetOn: iso(values.conditionMetOn)
      },
      fee: { rateBp: Math.round(Number(values.feePercent || 0) * 100) },
      note: values.note
    };
    try {
      const res = guarantee
        ? await updateGuarantee(guarantee._id, body)
        : await createGuarantee(body);
      Success(t("Saved"));
      changed(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  const act = async (fn, message) => {
    try {
      const res = await fn();
      if (message) Success(message);
      if (res?.data?.data) changed(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    }
  };

  const letter = force =>
    act(() => createReturnLetter(guarantee._id, { force }), t("Letter created"));
  const emailLetter = () => {
    let to = guarantee.clientInfo?.email || "";
    Modal.confirm({
      rootClassName: "warranty-page",
      title: t("Send the letter by email"),
      content: (
        <Input
          type="email"
          defaultValue={to}
          onChange={e => (to = e.target.value)}
          placeholder={t("Email address")}
        />
      ),
      okText: t("Send"),
      cancelText: t("Cancel"),
      onOk: () => act(() => emailReturnLetter(guarantee._id, { to }), t("Sent"))
    });
  };

  const warranties = (meta?.warranties || []).filter(
    w => !projectId || !w.project || w.project === projectId
  );
  const editable = !guarantee || guarantee.status !== "returned";
  const canEdit = guarantee ? meta?.can.update : meta?.can.create;
  const g = guarantee;

  return (
    <Modal
      rootClassName="warranty-page"
      open={open}
      width={820}
      title={
        g ? (
          <Space wrap>
            {guaranteeTypeLabels(t)[g.type]} {g.number}
            <Tag color={GUARANTEE_COLORS[g.status]}>{labels[g.status]}</Tag>
          </Space>
        ) : (
          t("New guarantee bond")
        )
      }
      onCancel={onClose}
      footer={[
        g && meta?.can.delete && !g.retentionInvoices?.length && (
          <Popconfirm
            key="delete"
            title={t("Delete this guarantee bond?")}
            onConfirm={() =>
              deleteGuarantee(g._id)
                .then(() => {
                  onChanged?.();
                  onClose();
                })
                .catch(generalErrorAlert)
            }
          >
            <Button danger>{t("Delete")}</Button>
          </Popconfirm>
        ),
        <Button key="close" onClick={onClose}>
          {t("Close")}
        </Button>,
        canEdit && editable && (
          <Button key="save" type="primary" loading={busy} onClick={() => form.submit()}>
            {t("Save")}
          </Button>
        )
      ].filter(Boolean)}
    >
      {g && (
        <>
          <Descriptions size="small" column={{ xs: 1, md: 2 }} className="mb-3">
            <Descriptions.Item label={t("Due back")}>
              {g.returnDue ? (
                <Space wrap size={4}>
                  <span>{formatDay(g.returnDue)}</span>
                  <Typography.Text type="secondary">
                    {sourceLabel(t, g.due?.source)}
                  </Typography.Text>
                  {g.returnOverdue && <Tag color="error">{t("Due back")}</Tag>}
                </Space>
              ) : (
                reasons[g.due?.reason] || "—"
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("Warranty")}>
              {g.warrantyInfo
                ? `${g.warrantyInfo.title}${g.warrantyInfo.end ? ` · ${t("ends {{date}}", { date: formatDay(g.warrantyInfo.end) })}` : ""}`
                : "—"}
            </Descriptions.Item>
            {g.fee?.rateBp > 0 && (
              <Descriptions.Item label={t("Guarantee fee")}>
                {t("{{yearly}} per year · {{accrued}} so far", {
                  yearly: formatEuro(g.fee.yearly, lng),
                  accrued: formatEuro(g.fee.accrued, lng)
                })}
              </Descriptions.Item>
            )}
            {g.status === "drawn" && (
              <Descriptions.Item label={t("Amount drawn")}>
                {formatEuro(g.drawn?.amount, lng)} ({formatDay(g.drawn?.on)})
              </Descriptions.Item>
            )}
            {g.returnedOn && (
              <Descriptions.Item label={t("Returned on")}>
                {formatDay(g.returnedOn)}
              </Descriptions.Item>
            )}
            {g.retentionInvoiceInfo?.length > 0 && (
              <Descriptions.Item label={t("Replaces the retention of")}>
                {g.retentionInvoiceInfo.map(i => i.invoiceNumber || i.title).join(", ")}
              </Descriptions.Item>
            )}
          </Descriptions>

          {meta?.can.update && (
            <Space wrap className="mb-3">
              {g.direction === "given" && ["active", "return_requested"].includes(g.status) && (
                <>
                  {g.hasLetter ? (
                    <>
                      <Button
                        icon={<FilePdfOutlined />}
                        href={returnLetterUrl(g._id)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("Return letter")}
                      </Button>
                      <Button icon={<MailOutlined />} onClick={emailLetter}>
                        {t("Email the letter")}
                      </Button>
                      <Button onClick={() => letter(true)}>{t("Create again")}</Button>
                    </>
                  ) : g.returnOverdue ? (
                    <Button type="primary" onClick={() => letter(false)}>
                      {t("Create return letter")}
                    </Button>
                  ) : (
                    g.returnDue && (
                      <Popconfirm
                        title={t("The bond is not due back yet. Create the letter anyway?")}
                        onConfirm={() => letter(true)}
                      >
                        <Button>{t("Create return letter")}</Button>
                      </Popconfirm>
                    )
                  )}
                </>
              )}
              {g.status !== "returned" && (
                <Button onClick={() => setStatusDialog("returned")}>{t("Mark as returned")}</Button>
              )}
              {g.status === "active" && g.direction === "given" && (
                <Button onClick={() => setStatusDialog("return_requested")}>
                  {t("Return requested")}
                </Button>
              )}
              {g.status !== "drawn" && g.status !== "returned" && (
                <Button danger onClick={() => setStatusDialog("drawn")}>
                  {t("Drawn")}
                </Button>
              )}
              {g.status !== "active" && (
                <Button
                  onClick={() =>
                    act(() => setGuaranteeStatus(g._id, { status: "active" }), t("Saved"))
                  }
                >
                  {t("Active again")}
                </Button>
              )}
            </Space>
          )}
          {g.direction === "given" && (
            <Alert
              className="mb-3"
              type="warning"
              showIcon
              message={t(
                "The letter wording is a draft. Have it checked by your lawyer before you use it for the first time."
              )}
            />
          )}

          {meta?.can.replaceRetention &&
            g.direction === "given" &&
            g.type === "warranty" &&
            g.status === "active" &&
            candidates.length > 0 && (
              <Alert
                className="mb-3"
                type="info"
                message={t("Replace a retention with this bond")}
                description={
                  <Space direction="vertical" className="wty-full">
                    <Typography.Text type="secondary">
                      {t(
                        "The customer then has to pay the retained amount from the day the bond was handed over."
                      )}
                    </Typography.Text>
                    <Checkbox.Group
                      value={chosen}
                      onChange={setChosen}
                      options={candidates.map(i => ({
                        value: i._id,
                        label: `${i.invoiceNumber || i.title} · ${formatEuro(i.retention.amount, lng)}`
                      }))}
                    />
                    <Button
                      size="small"
                      type="primary"
                      disabled={!chosen.length}
                      onClick={() =>
                        act(() => replaceRetention(g._id, chosen), t("Retention replaced"))
                      }
                    >
                      {t("Replace retention")}
                    </Button>
                  </Space>
                }
              />
            )}
          <Divider className="wty-divider" />
        </>
      )}

      <Form form={form} layout="vertical" onFinish={submit} disabled={!canEdit || !editable}>
        <Row gutter={[12, 0]}>
          <Col xs={24} md={12}>
            <Form.Item name="direction" label={t("Direction")}>
              <Radio.Group
                optionType="button"
                options={Object.entries(directionLabels(t)).map(([value, label]) => ({
                  value,
                  label
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="type" label={t("Type")}>
              <Select
                options={Object.entries(guaranteeTypeLabels(t)).map(([value, label]) => ({
                  value,
                  label
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="issuerName"
              label={t("Bank or insurer")}
              rules={[{ required: true, whitespace: true }]}
            >
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="bondNumber" label={t("Bond number")}>
              <Input maxLength={100} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="amount" label={t("Amount")} rules={[{ required: true }]}>
              <InputNumber
                className="wty-full"
                min={0.01}
                precision={2}
                addonAfter="€"
                {...decimalInputProps(lng)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="contractBasis" label={t("Contract basis")}>
              <Select
                options={Object.entries(basisLabels(t)).map(([value, label]) => ({ value, label }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="project" label={t("Project")}>
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={(meta?.projects || []).map(p => ({ value: p._id, label: p.name }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="warranty"
              label={t("Warranty")}
              extra={t("The return date follows from its acceptance.")}
            >
              <Select
                showSearch
                allowClear
                optionFilterProp="label"
                options={warranties.map(w => ({ value: w._id, label: w.title }))}
              />
            </Form.Item>
          </Col>
          {direction === "received" ? (
            <Col xs={24} md={12}>
              <Form.Item name="supplier" label={t("Subcontractor")}>
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={(meta?.suppliers || []).map(s => ({ value: s._id, label: s.name }))}
                />
              </Form.Item>
            </Col>
          ) : (
            <Col xs={24} md={12}>
              <Form.Item name="client" label={t("Customer")}>
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  options={(meta?.clients || []).map(c => ({ value: c._id, label: c.name }))}
                />
              </Form.Item>
            </Col>
          )}
          <Col xs={24} md={12}>
            <Form.Item name="beneficiary" label={t("Beneficiary (holds the bond)")}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="principal" label={t("Principal debtor")}>
              <Input maxLength={200} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name="issuerAddress" label={t("Address of the bank or insurer")}>
              <Input maxLength={500} />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="issueDate" label={t("Issued on")}>
              <DatePicker className="wty-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="handedOverOn" label={t("Handed over on")}>
              <DatePicker className="wty-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="expiryDate" label={t("Expires on")} extra={t("Empty = unlimited")}>
              <DatePicker className="wty-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          {type === "advance_payment" ? (
            <Col xs={12} md={8}>
              <Form.Item name="conditionMetOn" label={t("Advance offset on")}>
                <DatePicker className="wty-full" format="DD.MM.YYYY" />
              </Form.Item>
            </Col>
          ) : (
            <Col xs={12} md={8}>
              <Form.Item name="agreedMonths" label={t("Agreed return after (months)")}>
                <InputNumber className="wty-full" min={0} max={360} precision={0} />
              </Form.Item>
            </Col>
          )}
          <Col xs={12} md={8}>
            <Form.Item name="agreedDate" label={t("Agreed return date")}>
              <DatePicker className="wty-full" format="DD.MM.YYYY" />
            </Form.Item>
          </Col>
          <Col xs={12} md={8}>
            <Form.Item name="feePercent" label={t("Guarantee fee % p.a.")}>
              <InputNumber
                className="wty-full"
                min={0}
                max={100}
                precision={2}
                {...decimalInputProps(lng)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="note" label={t("Note")}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} maxLength={5000} />
        </Form.Item>
      </Form>

      <StatusDialog
        open={Boolean(statusDialog)}
        status={statusDialog}
        guarantee={g}
        onClose={() => setStatusDialog(null)}
        onDone={data => {
          setStatusDialog(null);
          changed(data);
        }}
      />
    </Modal>
  );
};

export default GuaranteeModal;
